/**
 * MongoDB implementation of Organisation Repository
 * 
 * Clean infrastructure implementation
 */
import { MongoClient, Db, Collection } from 'mongodb';
import { Organisation } from '../../bounded-contexts/parties/domain/aggregates/Organisation';
import { IOrganisationRepository } from '../../bounded-contexts/parties/domain/repositories/IOrganisationRepository';
import { v4 as uuidv4 } from 'uuid';

export class MongoOrganisationRepository implements IOrganisationRepository {
  private db: Db;
  private collection: Collection;
  private client: MongoClient;

  constructor(
    private readonly mongoUrl: string,
    private readonly dbName: string
  ) {}

  private async ensureConnection(): Promise<void> {
    if (!this.client) {
      this.client = new MongoClient(this.mongoUrl);
      await this.client.connect();
      this.db = this.client.db(this.dbName);
      this.collection = this.db.collection('organisations');
      
      // Create indexes
      await this.collection.createIndex({ registrationNumber: 1 }, { unique: true, sparse: true });
      await this.collection.createIndex({ industry: 1 });
      await this.collection.createIndex({ parentOrganisationId: 1 });
      await this.collection.createIndex({ isActive: 1 });
      await this.collection.createIndex({ legalName: 'text', tradingName: 'text' });
    }
  }

  async save(organisation: Organisation): Promise<void> {
    await this.ensureConnection();
    const snapshot = organisation.toSnapshot();
    
    await this.collection.replaceOne(
      { _id: snapshot.id },
      { _id: snapshot.id, ...snapshot },
      { upsert: true }
    );
  }

  async findById(id: string): Promise<Organisation | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ _id: id });
    
    if (!doc) return null;
    
    return Organisation.fromSnapshot(doc);
  }

  async findByRegistrationNumber(registrationNumber: string): Promise<Organisation | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ registrationNumber });
    
    if (!doc) return null;
    
    return Organisation.fromSnapshot(doc);
  }

  async findByIndustry(industry: string): Promise<Organisation[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      industry,
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Organisation.fromSnapshot(doc));
  }

  async findByParentOrganisation(parentId: string): Promise<Organisation[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      parentOrganisationId: parentId,
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Organisation.fromSnapshot(doc));
  }

  async findActive(): Promise<Organisation[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Organisation.fromSnapshot(doc));
  }

  async search(criteria: {
    name?: string;
    industry?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<Organisation[]> {
    await this.ensureConnection();
    
    const query: any = {};
    
    if (criteria.name) {
      query.$text = { $search: criteria.name };
    }
    
    if (criteria.industry !== undefined) {
      query.industry = criteria.industry;
    }
    
    if (criteria.isActive !== undefined) {
      query.isActive = criteria.isActive;
    }
    
    const cursor = this.collection.find(query);
    
    if (criteria.offset) {
      cursor.skip(criteria.offset);
    }
    
    if (criteria.limit) {
      cursor.limit(criteria.limit);
    }
    
    const docs = await cursor.toArray();
    
    return docs.map(doc => Organisation.fromSnapshot(doc));
  }

  async remove(id: string): Promise<void> {
    await this.ensureConnection();
    // Soft delete - mark as inactive
    await this.collection.updateOne(
      { _id: id },
      { 
        $set: { 
          isActive: false,
          updatedAt: new Date() 
        } 
      }
    );
  }

  nextIdentity(): string {
    return uuidv4();
  }

  async disconnect(): Promise<void> {
    if (this.client) {
      await this.client.close();
    }
  }
}